import { activateTab, TAB_NAV_EVENT, updateUrl } from "../Tabbed";
import { AbstractFormElement } from "../../Components/Form/Abstract";
import { InputText } from "../../Components/Form/Input";
import { Checkbox } from "../../Components/Form/Checkbox";
import { RadioGroup } from "../../Components/Form/RadioGroup";
import { Zip } from "../../Components/Form/Zip";
import { ISpareParts } from "../../Interfaces/Appointments";
import LoaderOverlay from "../LoaderOverlay";
import { formatThousandSeparator } from "../../Utils/Number";
import { ABSTRACT } from "./Constants";

interface IState {
    request: { [key: string]: string } | ISpareParts;
    step: NonNullable<unknown>;
    completed: NonNullable<unknown>;
}

abstract class FormAbstract {
    declare form: HTMLFormElement;
    protected readonly loader: LoaderOverlay;
    private readonly alreadyCustomer: Checkbox;
    private readonly street: InputText;
    private readonly zipCode: Zip;
    private readonly city: InputText;
    declare backBtn: HTMLButtonElement;
    declare cancelBtn: HTMLButtonElement;
    declare nextBtn: HTMLButtonElement;
    declare submitBtn: HTMLButtonElement;
    protected state: IState;
    private _key: string;
    protected tabs = document.querySelectorAll(ABSTRACT.SELECTORS.tabs) as NodeListOf<HTMLElement>;
    protected forms = document.querySelectorAll(ABSTRACT.SELECTORS.form) as NodeListOf<HTMLFormElement>;
    private activeTabId: string;

    protected constructor(form: HTMLFormElement) {
        this.form = form;
        this.loader = new LoaderOverlay();
        this.backBtn = this.form.querySelector(ABSTRACT.SELECTORS.back);
        this.cancelBtn = this.form.querySelector(ABSTRACT.SELECTORS.cancel);
        this.nextBtn = this.form.querySelector(ABSTRACT.SELECTORS.next);
        this.submitBtn = this.form.querySelector(ABSTRACT.SELECTORS.submit);
        this.alreadyCustomer = this.form.querySelector<Checkbox>(ABSTRACT.SELECTORS.already_customer);
        this.street = this.form.querySelector<InputText>(ABSTRACT.SELECTORS.street);
        this.zipCode = this.form.querySelector<Zip>(ABSTRACT.SELECTORS.zip_code);
        this.city = this.form.querySelector<InputText>(ABSTRACT.SELECTORS.city);
    }

    initListeners() {
        this.clearSessionStorageData();
        this.backBtn?.addEventListener("click", this.back.bind(this));
        this.nextBtn?.addEventListener("click", this.next.bind(this));
        this.alreadyCustomer?.addEventListener("change", this.handleAlreadyCustomer.bind(this));
        if (this.form.classList.contains("last")) {
            this.submitBtn?.addEventListener("click", (e) => this.submit(e));
        }

        for (const element in Array.from(this.form.elements)) {
            const formElement = this.form.elements[element] as AbstractFormElement;

            if (formElement instanceof InputText && formElement.inputMode === "decimal") {
                formElement.addEventListener("input", () => formatThousandSeparator(formElement));
            }
            if (formElement instanceof Checkbox) {
                formElement.addEventListener("change", () => {
                    formElement.value = formElement.checked ? "1" : "0";
                    const formData = this.getFormData(this.form);
                    formData.set(formElement.name, formElement.value);
                    this.updateState(formData);
                });
            }
            if (formElement instanceof RadioGroup) {
                formElement.addEventListener("click", () => {
                    this.updateState(this.getFormData(this.form));
                });
            }
            formElement.addEventListener("change", () => {
                this.updateState(this.getFormData(this.form));
            });
            formElement.addEventListener("input", () => {
                this.updateState(this.getFormData(this.form));
            });
        }

        return this;
    }

    back(e: MouseEvent) {
        e.preventDefault();

        const activeTab = document.querySelector(ABSTRACT.SELECTORS.tabs + ".active") as HTMLElement;
        const previousTab = activeTab?.previousElementSibling as HTMLElement;
        const previousTabId = previousTab?.dataset.tab;

        this.jumpToTab(previousTabId);
    }

    next(e: MouseEvent) {
        e.preventDefault();

        const activeTab = document.querySelector(ABSTRACT.SELECTORS.tabs + ".active") as HTMLElement;
        const nextTab = activeTab?.nextElementSibling as HTMLElement;
        const nextTabId = nextTab?.dataset.tab;
        const tab = nextTab.closest(".tabbed").querySelector(`[id*="tab-${nextTabId}"]`);

        if (this.form.checkValidity()) {
            this.jumpToTab(nextTabId);
            if (activeTab.dataset.tab !== "images") {
                activeTab.classList.add("completed");
            }

            tab.dispatchEvent(new CustomEvent(TAB_NAV_EVENT, { detail: activeTab }));
        }
        this.form.dispatchEvent(new CustomEvent("form:next"));
    }

    abstract submit(e: Event): Promise<void>;

    protected resetForms() {
        const firstTab = this.tabs.item(0).dataset.tab;
        this.jumpToTab(firstTab);
        this.clearSessionStorageData();
        this.tabs?.forEach((tab) => tab.classList.remove("completed"));
        this.forms?.forEach((form) => {
            form.reset();
            form.dispatchEvent(new CustomEvent("form:reset"));
        });
    }

    protected updateState(formData: FormData) {
        const sessionData = this.getSessionStorageData() || {
            request: {},
            step: {},
            completed: {}
        };
        const formDataObject = Object.fromEntries(formData) as { [key: string]: string };
        const tabId = this.form.closest(".step").id.replace("tab-", "");
        const stepInfo = { ...sessionData.step };
        const completed = tabId && { ...sessionData.completed, [tabId]: this.form.checkValidity() };

        this.state = {
            ...sessionData,
            step: stepInfo,
            request: { ...sessionData.request, ...formDataObject },
            completed
        };

        this.setSessionStorageData(this.state);
    }

    protected checkAllFormsValidity(): boolean {
        const forms = document.querySelectorAll(ABSTRACT.SELECTORS.form) as NodeListOf<HTMLFormElement>;
        const tabs = document.querySelectorAll(ABSTRACT.SELECTORS.tabs + ":not(.completed)") as NodeListOf<HTMLElement>;
        let invalidForm;
        let incompleteTab;
        for (const form of forms) {
            if (!form.checkValidity()) {
                invalidForm = form;
                break;
            }
        }
        if (tabs.length) {
            incompleteTab = tabs[0].dataset.tab;
        }
        const tabId = incompleteTab || invalidForm?.closest(".step")?.id.replace("tab-", "");
        if (tabId && tabId !== "personal-data") {
            invalidForm?.dispatchEvent(new CustomEvent("form:next"));
            this.jumpToTab(tabId);
        }
        return !!tabId;
    }

    private jumpToTab(tabId: string): void {
        const headerHeight = document.querySelector<HTMLElement>("body > header").offsetHeight;
        const top = document.querySelector("section.steps").getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ behavior: "smooth", top: top, left: 0 });
        this.activeTabId = tabId;
        activateTab(tabId);
        updateUrl(tabId);
    }

    handleAlreadyCustomer() {
        if (this.street) {
            this.street.disabled = this.alreadyCustomer.checked;
            this.street.classList.toggle("hide-me", this.alreadyCustomer.checked);
        }
        if (this.zipCode) {
            this.zipCode.disabled = this.alreadyCustomer.checked;
            this.zipCode.classList.toggle("hide-me", this.alreadyCustomer.checked);
        }
        if (this.city) {
            this.city.disabled = this.alreadyCustomer.checked;
            this.city.classList.toggle("hide-me", this.alreadyCustomer.checked);
        }
    }

    protected getFormData(form: HTMLFormElement) {
        return new FormData(form);
    }

    static activateTab(tabId: string) {
        activateTab(tabId);
    }

    static updateUrl(tabId: string) {
        updateUrl(tabId);
    }

    protected getSessionStorageData(): IState {
        return JSON.parse(sessionStorage.getItem(this.key));
    }

    protected setSessionStorageData(value: IState) {
        sessionStorage.setItem(this.key, JSON.stringify(value));
    }

    protected clearSessionStorageData() {
        sessionStorage.removeItem(this.key);
    }

    get key(): string {
        return this._key;
    }

    set key(value: string) {
        this._key = value;
    }
}

export { FormAbstract, IState };
